/**
 * Application des profils de calques (v1 rando + v2 vélo avec couleur / thèmes).
 */
import type { MapRef } from '@vis.gl/react-maplibre'
import type {
  ExpressionSpecification,
  FilterSpecification,
  LayerSpecification,
  Map as MaplibreMap,
} from 'maplibre-gl'
import {
  buildClassLineColorExpression,
  mergeClassFilter,
  normalizeOsmClass,
} from './mapLayerClassStyle'
import { mergeFeatureMatchFilters } from './mapLayerKindStyle'
import { resolveLineWidth, type MapLayerEditorSettings, type MapLayerProfileConfig } from './mapEditorConfig'
import {
  applyInjectedLayers,
  canInjectLayerSettings,
  clearInjectLayerCache,
  ensureInjectedLayersPresent,
  normalizeInjectLayerId,
  resetInjectedLayers,
} from './mapLayerInject'

/** @vis.gl/react-maplibre omet setPaintProperty sur MapRef — utiliser l'instance MapLibre native. */
export function resolveMaplibreMap(map: MaplibreMap | MapRef | null | undefined): MaplibreMap | null {
  if (!map) return null
  if (typeof (map as MapRef).getMap === 'function') {
    // getMap() peut être undefined tant que la Map n’est pas montée
    return (map as MapRef).getMap() ?? null
  }
  return map as MaplibreMap
}

/** True seulement si `map.style` existe et est chargé — sinon getSource/getLayer plantent. */
export function isMapStyleReady(map: MaplibreMap | null | undefined): map is MaplibreMap {
  if (!map) return false
  try {
    const style = (map as MaplibreMap & { style?: unknown }).style
    if (!style) return false
    return Boolean(map.isStyleLoaded())
  } catch {
    return false
  }
}

/** getSource sans throw si le style n’est pas encore attaché (WebView / swap de style). */
export function safeGetSource(map: MaplibreMap, id: string) {
  try {
    if (!(map as MaplibreMap & { style?: unknown }).style) return undefined
    return map.getSource(id)
  } catch {
    return undefined
  }
}

/** getLayer sans throw si le style n’est pas prêt. */
export function safeGetLayer(map: MaplibreMap, id: string) {
  try {
    if (!(map as MaplibreMap & { style?: unknown }).style) return undefined
    return map.getLayer(id)
  } catch {
    return undefined
  }
}

/** Chemin piéton (non pavé) vs Piéton (pavé / sans paved) — même couleur si seul Piéton est stylé. */
function effectiveLayerSettings(
  layerId: string,
  settings: MapLayerEditorSettings,
  layers: Record<string, MapLayerEditorSettings>,
): MapLayerEditorSettings {
  if (layerId !== 'Path') return settings
  const pedestrian = layers.Pedestrian
  if (!pedestrian?.lineColor || settings.lineColor) return settings
  return { ...settings, lineColor: pedestrian.lineColor }
}

const SOLID_DASHARRAY: ExpressionSpecification = ['literal', [1000, 0]]

export type LineWidthSpec =
  | number
  | ExpressionSpecification
  | { base?: number; stops: [number, number][] }
type LineDashSpec = ExpressionSpecification | number[] | undefined

const originalWidthsByStyle = new Map<string, Map<string, LineWidthSpec | undefined>>()
const originalDashByStyle = new Map<string, Map<string, LineDashSpec>>()
const originalColorByStyle = new Map<string, Map<string, unknown>>()
const originalVisibilityByStyle = new Map<string, Map<string, string>>()
const originalFiltersByStyle = new Map<string, Map<string, FilterSpecification | undefined>>()

/** Multiplie une valeur de sortie (nombre ou sous-expression sans ["zoom"]). */
function scaleWidthOutput(value: unknown, factor: number): unknown {
  if (typeof value === 'number') return Math.max(value * factor, 2)
  if (Array.isArray(value)) return ['*', value, factor]
  return value
}

export function boostLineWidth(value: LineWidthSpec | undefined, factor: number): LineWidthSpec {
  if (value == null) {
    return ['interpolate', ['linear'], ['zoom'], 11, 3, 14, 5, 16, 7, 18, 10]
  }
  if (typeof value === 'number') return Math.max(value * factor, 2)
  if (Array.isArray(value)) {
    // ["zoom"] n'est valide qu'en entrée directe d'un interpolate/step de
    // premier niveau : on multiplie les sorties, jamais l'expression entière.
    const op = value[0]
    if (op === 'interpolate' || op === 'interpolate-hcl' || op === 'interpolate-lab') {
      // ['interpolate', interp, input, stop1, out1, stop2, out2, ...]
      const out: unknown[] = value.slice(0, 3)
      for (let i = 3; i + 1 < value.length; i += 2) {
        out.push(value[i], scaleWidthOutput(value[i + 1], factor))
      }
      return out as ExpressionSpecification
    }
    if (op === 'step') {
      // ['step', input, base, stop1, out1, ...]
      const out: unknown[] = [value[0], value[1], scaleWidthOutput(value[2], factor)]
      for (let i = 3; i + 1 < value.length; i += 2) {
        out.push(value[i], scaleWidthOutput(value[i + 1], factor))
      }
      return out as ExpressionSpecification
    }
    return ['*', value, factor]
  }
  if (typeof value === 'object' && 'stops' in value) {
    const base = value.base ?? 1
    return {
      base,
      stops: value.stops.map(([z, w]) => [z, Math.max(w * factor, 2)] as [number, number]),
    }
  }
  return value
}

function getCaches(styleKey: string, layers: LayerSpecification[]) {
  let widths = originalWidthsByStyle.get(styleKey)
  let dashes = originalDashByStyle.get(styleKey)
  let colors = originalColorByStyle.get(styleKey)
  let visibilities = originalVisibilityByStyle.get(styleKey)
  let filters = originalFiltersByStyle.get(styleKey)
  if (!widths) widths = new Map()
  if (!dashes) dashes = new Map()
  if (!colors) colors = new Map()
  if (!visibilities) visibilities = new Map()
  if (!filters) filters = new Map()

  for (const layer of layers) {
    if (layer.type !== 'line') continue
    if (!widths.has(layer.id)) widths.set(layer.id, layer.paint?.['line-width'] as LineWidthSpec | undefined)
    if (!dashes.has(layer.id)) dashes.set(layer.id, layer.paint?.['line-dasharray'] as LineDashSpec)
    if (!colors.has(layer.id)) colors.set(layer.id, layer.paint?.['line-color'])
    if (!filters.has(layer.id)) filters.set(layer.id, layer.filter as FilterSpecification | undefined)
    if (!visibilities.has(layer.id)) {
      visibilities.set(layer.id, layer.layout?.visibility === 'none' ? 'none' : 'visible')
    }
  }

  originalWidthsByStyle.set(styleKey, widths)
  originalDashByStyle.set(styleKey, dashes)
  originalColorByStyle.set(styleKey, colors)
  originalVisibilityByStyle.set(styleKey, visibilities)
  originalFiltersByStyle.set(styleKey, filters)
  return { widths, dashes, colors, visibilities, filters }
}

function ruleAppliesToTheme(themeKeys: string[] | undefined, currentThemeKey: string): boolean {
  if (!themeKeys || themeKeys.length === 0) return true
  return themeKeys.includes(currentThemeKey)
}

function findLayersForRule(
  layers: LayerSpecification[],
  layerId: string,
  sourceLayer?: string,
): LayerSpecification[] {
  const byId = layers.filter((l) => l.id === layerId && l.type === 'line')
  if (byId.length > 0) return byId
  if (!sourceLayer) return []
  return layers.filter((l) => l.type === 'line' && l['source-layer'] === sourceLayer)
}

function applyV1(
  map: MaplibreMap,
  config: MapLayerProfileConfig,
  styleKey: string,
  layers: LayerSpecification[],
  caches: ReturnType<typeof getCaches>,
): number {
  const layersById = new Map(layers.map((l) => [l.id, l]))
  const widthFactor =
    typeof config.widthFactor === 'number' && config.widthFactor > 0 ? config.widthFactor : 1
  let applied = 0

  const applyOne = (
    lid: string,
    layer: LayerSpecification,
    settings: {
      enabled: boolean
      solid?: boolean
      thicken?: boolean
      lineColor?: string
      lineWidth?: number
      lineWidthZ1?: number
      lineWidthPx1?: number
      lineWidthZ2?: number
      lineWidthPx2?: number
      minZoom?: number
      sourceLayer?: string
      matchClass?: string
      matchKind?: string
      matchKindDetail?: string
      classColors?: Record<string, string>
      classAliases?: Record<string, string>
    },
  ) => {
    if (layer.type !== 'line') return
    try {
      if (!settings.enabled) {
        map.setLayoutProperty(lid, 'visibility', 'none')
        applied++
        return
      }
      const origVis = caches.visibilities.get(lid) ?? 'visible'
      map.setLayoutProperty(lid, 'visibility', origVis === 'none' ? 'visible' : origVis)

      const matchClass = settings.matchClass ? normalizeOsmClass(settings.matchClass) : ''
      const origFilter = caches.filters.get(lid)
      const mergedFilter = mergeFeatureMatchFilters(
        origFilter,
        settings.matchKind,
        settings.matchKindDetail,
        matchClass || undefined,
      )
      if (mergedFilter !== undefined) {
        map.setFilter(lid, mergedFilter)
      } else if (origFilter !== undefined) {
        map.setFilter(lid, origFilter)
      }

      // Largeur : px@z explicite > épaissir (thicken × widthFactor, comme l'éditeur panel) > style d'origine
      const resolvedWidth = resolveLineWidth(settings)
      if (resolvedWidth != null) {
        map.setPaintProperty(lid, 'line-width', resolvedWidth)
      } else if (settings.thicken && widthFactor > 0) {
        map.setPaintProperty(
          lid,
          'line-width',
          boostLineWidth(caches.widths.get(lid), widthFactor),
        )
      } else if (caches.widths.has(lid)) {
        const w = caches.widths.get(lid)
        if (w !== undefined) map.setPaintProperty(lid, 'line-width', w)
      }

      if (settings.solid) {
        map.setPaintProperty(lid, 'line-dasharray', SOLID_DASHARRAY)
      } else if (caches.dashes.has(lid)) {
        const d = caches.dashes.get(lid)
        if (d !== undefined) map.setPaintProperty(lid, 'line-dasharray', d)
      }

      const classColorKeys = settings.classColors ? Object.keys(settings.classColors) : []
      if (classColorKeys.length > 0) {
        map.setPaintProperty(
          lid,
          'line-color',
          buildClassLineColorExpression(
            settings.classColors!,
            settings.classAliases,
            settings.lineColor,
            caches.colors.get(lid),
          ),
        )
      } else if (settings.lineColor) {
        map.setPaintProperty(lid, 'line-color', settings.lineColor)
      } else if (caches.colors.has(lid)) {
        const c = caches.colors.get(lid)
        if (c !== undefined) map.setPaintProperty(lid, 'line-color', c)
      }

      // Zoom minimum d'apparition
      if (settings.minZoom != null) {
        map.setLayerZoomRange(lid, settings.minZoom, 24)
      }

      applied++
    } catch {
      /* ignore */
    }
  }

  const configuredLayerIds = new Set(Object.keys(config.layers ?? {}))

  const layerSettings = config.layers ?? {}

  for (const [layerId, settings] of Object.entries(layerSettings)) {
    const effective = effectiveLayerSettings(layerId, settings, layerSettings)
    const layer = layersById.get(layerId)
    if (layer) {
      applyOne(layerId, layer, effective)
      continue
    }
    if (canInjectLayerSettings(normalizeInjectLayerId(layerId), effective, layersById, map)) continue
    if (!settings.sourceLayer) continue
    // Fallback : ID absent + source-layer sans filtre inject — calques du style non catalogués.
    for (const l of layers) {
      if (l.type !== 'line' || l['source-layer'] !== settings.sourceLayer) continue
      if (configuredLayerIds.has(l.id)) continue
      applyOne(l.id, l, effectiveLayerSettings(l.id, effective, layerSettings))
    }
  }

  applied += applyInjectedLayers(map, layerSettings, layers)
  return applied
}

function applyV2(
  map: MaplibreMap,
  config: MapLayerProfileConfig,
  styleKey: string,
  themeKey: string,
  layers: LayerSpecification[],
  caches: ReturnType<typeof getCaches>,
): number {
  let applied = 0
  for (const rule of config.rules ?? []) {
    if (!ruleAppliesToTheme(rule.themeKeys, themeKey)) continue
    const targets = findLayersForRule(layers, rule.layerId, rule.sourceLayer)
    for (const layer of targets) {
      const lid = layer.id
      try {
        if (!rule.enabled) {
          map.setLayoutProperty(lid, 'visibility', 'none')
          applied++
          continue
        }
        const origVis = caches.visibilities.get(lid) ?? 'visible'
        map.setLayoutProperty(lid, 'visibility', origVis === 'none' ? 'visible' : origVis)

        const matchClass = rule.matchClass ? normalizeOsmClass(rule.matchClass) : ''
        const origFilter = caches.filters.get(lid)
        if (matchClass) {
          map.setFilter(lid, mergeClassFilter(origFilter, matchClass))
        } else if (origFilter !== undefined) {
          map.setFilter(lid, origFilter)
        }

        // Largeur : zoom interpolation > fixe > conserver style d'origine
        const resolvedWidth = resolveLineWidth(rule)
        if (resolvedWidth != null) {
          map.setPaintProperty(lid, 'line-width', resolvedWidth)
        }

        if (rule.lineColor) {
          map.setPaintProperty(lid, 'line-color', rule.lineColor)
        } else if (caches.colors.has(lid)) {
          const c = caches.colors.get(lid)
          if (c !== undefined) map.setPaintProperty(lid, 'line-color', c)
        }

        if (rule.solid !== false) {
          map.setPaintProperty(lid, 'line-dasharray', SOLID_DASHARRAY)
        }
        applied++
      } catch {
        /* ignore */
      }
    }
  }
  return applied
}

let applyingMapLayerProfile = false
const appliedProfileKeyByMap = new WeakMap<MaplibreMap, string>()

export function clearMapLayerProfileState(map: MaplibreMap, resetInject = false): void {
  appliedProfileKeyByMap.delete(map)
  if (resetInject) {
    resetInjectedLayers(map)
  } else {
    clearInjectLayerCache(map)
  }
}

/** Restaure largeur / couleur / visibilité d'origine après un profil rando (pm_path, etc.). */
export function restoreMapLayerProfileStyle(map: MaplibreMap, styleKey: string): void {
  if (!map.isStyleLoaded()) return
  const layers = map.getStyle()?.layers ?? []
  const caches = getCaches(styleKey, layers)
  for (const layer of layers) {
    if (layer.type !== 'line') continue
    const lid = layer.id
    try {
      const vis = caches.visibilities.get(lid)
      if (vis !== undefined) map.setLayoutProperty(lid, 'visibility', vis)
      if (caches.widths.has(lid)) {
        const w = caches.widths.get(lid)
        if (w !== undefined) map.setPaintProperty(lid, 'line-width', w)
      }
      if (caches.dashes.has(lid)) {
        const d = caches.dashes.get(lid)
        if (d !== undefined) map.setPaintProperty(lid, 'line-dasharray', d)
      }
      if (caches.colors.has(lid)) {
        const c = caches.colors.get(lid)
        if (c !== undefined) map.setPaintProperty(lid, 'line-color', c)
      }
      if (caches.filters.has(lid)) {
        const f = caches.filters.get(lid)
        if (f !== undefined) map.setFilter(lid, f)
      }
    } catch {
      // calque absent ou style en transition
    }
  }
  clearMapLayerProfileState(map, true)
}

export function applyMapLayerProfile(
  mapLike: MaplibreMap | MapRef | null | undefined,
  styleKey: string,
  themeKey: string,
  config?: MapLayerProfileConfig | null,
): number {
  const map = resolveMaplibreMap(mapLike)
  if (!isMapStyleReady(map) || !config || applyingMapLayerProfile) return 0

  const layers = map.getStyle()?.layers ?? []
  const profileKey = `${styleKey}\0${themeKey}\0${JSON.stringify(config)}`
  const cachedKey = appliedProfileKeyByMap.get(map)
  if (cachedKey === profileKey) {
    const layerSettings = config.layers ?? {}
    return ensureInjectedLayersPresent(map, layerSettings, layers)
  }

  applyingMapLayerProfile = true
  const caches = getCaches(styleKey, layers)

  try {
    if (config.version === 2 && (config.rules?.length ?? 0) > 0) {
      const v2Applied = applyV2(map, config, styleKey, themeKey, layers, caches)
      const rulesAsLayers: Record<string, MapLayerEditorSettings> = {}
      for (const rule of config.rules ?? []) {
        if (!ruleAppliesToTheme(rule.themeKeys, themeKey)) continue
        rulesAsLayers[rule.layerId] = {
          enabled: rule.enabled,
          solid: rule.solid ?? true,
          lineColor: rule.lineColor,
          lineWidth: rule.lineWidth,
          lineWidthZ1: rule.lineWidthZ1,
          lineWidthPx1: rule.lineWidthPx1,
          lineWidthZ2: rule.lineWidthZ2,
          lineWidthPx2: rule.lineWidthPx2,
          sourceLayer: rule.sourceLayer,
          matchClass: rule.matchClass,
        }
      }
      const v2Total = v2Applied + applyInjectedLayers(map, rulesAsLayers, layers)
      appliedProfileKeyByMap.set(map, profileKey)
      return v2Total
    }
    const applied = applyV1(map, config, styleKey, layers, caches)
    appliedProfileKeyByMap.set(map, profileKey)
    return applied
  } finally {
    applyingMapLayerProfile = false
  }
}
